// Bot Protection for Payment Forms
// Blocks automated card testing attacks with 4 layers of defense

const RECAPTCHA_SITE_KEY = process.env.RECAPTCHA_SITE_KEY;
const MAX_ATTEMPTS = 3;
const RATE_LIMIT_WINDOW_MS = 5 * 60 * 1000; // 5 minutes
const MIN_TIME_ON_PAGE_MS = 3000; // 3 seconds

let formMountedAt = 0;

const botProtection = {
  // Load reCAPTCHA v3 script dynamically
  loadRecaptchaScript() {
    if (!RECAPTCHA_SITE_KEY || document.getElementById('recaptcha-script')) return;
    const script = document.createElement('script');
    script.id = 'recaptcha-script';
    script.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);
  },

  // Layer 3: Record when form mounts
  recordFormMount() {
    formMountedAt = Date.now();
  },

  // Layer 1: Execute reCAPTCHA v3 and require valid token
  async executeRecaptcha(action = 'payment') {
    if (typeof window.grecaptcha === 'undefined') {
      console.warn('reCAPTCHA not loaded');
      throw new Error('Security verification unavailable. Please refresh the page and try again.');
    }
    try {
      const token = await window.grecaptcha.execute(RECAPTCHA_SITE_KEY, { action });
      if (!token) {
        throw new Error('Security verification failed. Please try again.');
      }
      return token;
    } catch (err) {
      throw new Error('Security verification failed. Please refresh the page and try again.');
    }
  },

  // Layer 2: Check honeypot fields
  checkHoneypot(formData) {
    // These fields are hidden via CSS — real users never fill them
    if (formData.website || formData.phone_confirm) {
      throw new Error('Submission blocked.');
    }
  },

  // Layer 3: Check minimum time on page
  checkTimeOnPage() {
    const elapsed = Date.now() - formMountedAt;
    if (formMountedAt > 0 && elapsed < MIN_TIME_ON_PAGE_MS) {
      throw new Error('Please take a moment to review your information before submitting.');
    }
  },

  // Layer 4: Rate limit payment attempts
  checkRateLimit() {
    const now = Date.now();
    let attempts = [];
    try {
      attempts = JSON.parse(sessionStorage.getItem('_pa') || '[]');
    } catch (e) {
      attempts = [];
    }
    // Remove expired attempts
    attempts = attempts.filter(t => now - t < RATE_LIMIT_WINDOW_MS);
    if (attempts.length >= MAX_ATTEMPTS) {
      throw new Error('Too many payment attempts. Please wait a few minutes before trying again.');
    }
    attempts.push(now);
    sessionStorage.setItem('_pa', JSON.stringify(attempts));
  },

  // Run all checks (call before payment submission)
  async verify(honeypotData = {}) {
    this.checkHoneypot(honeypotData);
    this.checkTimeOnPage();
    this.checkRateLimit();
    return await this.executeRecaptcha('payment');
  },
};

export default botProtection;
